/**
 * Shared rendering helpers for the multi-input forms used by SEO tools.
 * Pure presentational components; no business logic.
 */
import { Check } from "lucide-react";

export function FormSection({ title, hint, children }) {
    return (
        <div className="space-y-3" data-testid="form-section">
            <div>
                <h3 className="font-display font-semibold text-base text-bl">
                    {title}
                </h3>
                {hint && (
                    <p className="mt-1 text-xs text-bl-muted">{hint}</p>
                )}
            </div>
            <div className="space-y-2">{children}</div>
        </div>
    );
}

export function FieldLabel({ children, hint }) {
    return (
        <div>
            <label className="text-sm font-medium text-bl block mb-1.5">
                {children}
            </label>
            {hint && <p className="text-xs text-bl-muted mb-1">{hint}</p>}
        </div>
    );
}

/**
 * Mobile-friendly multi-select. Renders pill buttons with checkmarks; tap to
 * toggle. Replaces small checkboxes for thumbs-only use.
 */
export function PillMultiSelect({
    options,
    value = [],
    onChange,
    testidPrefix = "pill",
}) {
    const toggle = (val) => {
        if (value.includes(val)) onChange(value.filter((v) => v !== val));
        else onChange([...value, val]);
    };
    return (
        <div className="flex flex-wrap gap-2">
            {options.map((o) => {
                const selected = value.includes(o.value);
                return (
                    <button
                        key={o.value}
                        type="button"
                        onClick={() => toggle(o.value)}
                        className={
                            "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm transition-colors min-h-[40px] " +
                            (selected
                                ? "bg-bl-primary text-white border-bl-primary"
                                : "bg-bl-surface text-bl border-bl hover:border-bl-primary/50")
                        }
                        data-testid={`${testidPrefix}-${o.value}`}
                        aria-pressed={selected}
                    >
                        {selected && <Check className="h-3.5 w-3.5" />}
                        {o.label}
                    </button>
                );
            })}
        </div>
    );
}

/**
 * Single-select pill group — same UX as multi-select but exclusive.
 */
export function PillSingleSelect({
    options,
    value,
    onChange,
    testidPrefix = "pill-single",
}) {
    return (
        <div className="flex flex-wrap gap-2">
            {options.map((o) => {
                const selected = value === o.value;
                return (
                    <button
                        key={o.value}
                        type="button"
                        onClick={() => onChange(o.value)}
                        className={
                            "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm transition-colors min-h-[40px] " +
                            (selected
                                ? "bg-bl-primary text-white border-bl-primary"
                                : "bg-bl-surface text-bl border-bl hover:border-bl-primary/50")
                        }
                        data-testid={`${testidPrefix}-${o.value}`}
                        aria-pressed={selected}
                    >
                        {selected && <Check className="h-3.5 w-3.5" />}
                        {o.label}
                    </button>
                );
            })}
        </div>
    );
}

export function NumberField({
    label,
    value,
    onChange,
    placeholder,
    prefix,
    suffix,
    min,
    max,
    testid,
}) {
    return (
        <div>
            {label && (
                <label className="text-sm font-medium text-bl block mb-1.5">
                    {label}
                </label>
            )}
            <div className="relative">
                {prefix && (
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-bl-muted text-sm pointer-events-none select-none z-[1]">
                        {prefix}
                    </span>
                )}
                <input
                    type="number"
                    inputMode="numeric"
                    value={value ?? ""}
                    onChange={(e) =>
                        onChange(
                            e.target.value === ""
                                ? ""
                                : Number(e.target.value),
                        )
                    }
                    placeholder={placeholder}
                    min={min}
                    max={max}
                    className={
                        "bl-input w-full " +
                        (prefix ? "pl-9 " : "") +
                        (suffix ? "pr-12" : "")
                    }
                    data-testid={testid}
                />
                {suffix && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-bl-muted text-sm pointer-events-none">
                        {suffix}
                    </span>
                )}
            </div>
        </div>
    );
}

export function TextField({
    label,
    value,
    onChange,
    placeholder,
    testid,
    maxLength,
}) {
    return (
        <div>
            {label && (
                <label className="text-sm font-medium text-bl block mb-1.5">
                    {label}
                </label>
            )}
            <input
                type="text"
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                maxLength={maxLength}
                className="bl-input w-full"
                data-testid={testid}
            />
        </div>
    );
}

export function DateField({ label, value, onChange, testid }) {
    return (
        <div>
            {label && (
                <label className="text-sm font-medium text-bl block mb-1.5">
                    {label}
                </label>
            )}
            <input
                type="date"
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
                className="bl-input w-full"
                data-testid={testid}
            />
        </div>
    );
}
